import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import Colors from "../../../../Helper/ColorsConstant";
import classes from "./WidgetCardsLandscape.module.css";


export default function WidgetCardsLandscape({
    cardsData = [],
    minOpenTicketCount,
    autoRevertDurationinSec = 0,
    isDarkTheme = false,
    OpenTicketPlacement,
    widgetViewportWidth = 0,
}) {
    const parentRef = useRef(null);
    const gridRef = useRef(null);
    const cardRefs = useRef([]);
    const autoRevertTimerRef = useRef(null);
    const resizeDebounceRef = useRef(null);
    const lastCalcTimeRef = useRef(0);
    const initialStateRef = useRef(null);
    const isFirstCalculationRef = useRef(true);
    const calcRetryRef = useRef(0);
    const calcHandlerRef = useRef(null);
    const calcRunIdRef = useRef(0);
    const dataVersionRef = useRef(0);  // Track data version to prevent stale state restoration
    const isCalculatingRef = useRef(false);  // Track if calculation is in progress
    const hasUserInteractedRef = useRef(false);
    const layoutMeasureTokenRef = useRef(0);
    const appliedMeasureTokenRef = useRef(-1);
    const colors = Colors;
    const normalizedCardsData = useMemo(() => (Array.isArray(cardsData) ? cardsData : []), [cardsData]);

    const defaultWidthOfOpenCard = 290;
    const defaultOpenHeaderBlockPx = 19.7;

    const isOpenTicketPlacementTop = useMemo(
        () => String(OpenTicketPlacement || "bottom").toLowerCase() === "top",
        [OpenTicketPlacement]
    );
    const safeMinOpenCountForRender = useMemo(
        () => (Number.isFinite(Number(minOpenTicketCount)) ? Math.max(1, Math.min(5, Math.floor(Number(minOpenTicketCount)))) : 1),
        [minOpenTicketCount]
    );
    const estimatedTicketsToRender = useMemo(() => {
        const totalTickets = normalizedCardsData.length;
        if (totalTickets === 0) return 0;

        const minOpen = Math.min(safeMinOpenCountForRender, totalTickets);
        const containerPadPx = 2;
        const effectiveWidth = Math.max(0, widgetViewportWidth - (2 * containerPadPx));
        if (effectiveWidth <= 0) return minOpen;

        const estimatedCardSpacingPx = 3.5; // Average spacing based on measurements, can be refined further
        const estimatedOpenHeaderBlockPx = defaultOpenHeaderBlockPx;

        const estimatedOpenBodyPerTicketPx = defaultWidthOfOpenCard;

        const estimatedHighlyCollapsedHeaderBlockPx = 4;
        const estimatedMediumHeaderBlockPx = defaultOpenHeaderBlockPx;
        const perCollapsedTicketPx = estimatedHighlyCollapsedHeaderBlockPx + estimatedCardSpacingPx;
        const perMediumCollapsedTicketPx = estimatedMediumHeaderBlockPx + estimatedCardSpacingPx;


        let fittedOpenCount = minOpen;
        while (fittedOpenCount > 0) {
            const openTicketsPx = fittedOpenCount * (estimatedOpenHeaderBlockPx + estimatedOpenBodyPerTicketPx);
            const openSpacingPx = Math.max(0, fittedOpenCount - 1) * estimatedCardSpacingPx;
            if ((openTicketsPx + openSpacingPx) <= effectiveWidth) {
                break;
            }
            fittedOpenCount -= 1;
        }

        const reservedOpenTicketsPx = fittedOpenCount * (estimatedOpenHeaderBlockPx + estimatedOpenBodyPerTicketPx);
        const reservedOpenSpacingPx = Math.max(0, fittedOpenCount - 1) * estimatedCardSpacingPx;
        let remainingHeightPx = Math.max(0, effectiveWidth - (reservedOpenTicketsPx + reservedOpenSpacingPx));

        const baseTickets = fittedOpenCount;//+ mediumForOpenDeficit + collapsedForOpenDeficit;
        // Use existing balanced mix for any additional tickets after open/deficit allocation.
        const mixWeightMedium = 0.33;
        const mediumHeightBudget = remainingHeightPx * mixWeightMedium;
        const collapsedHeightBudget = Math.max(0, remainingHeightPx - mediumHeightBudget);
        const mediumCollapsedTickets = Math.max(0, Math.floor(mediumHeightBudget / perMediumCollapsedTicketPx));
        const additionalCollapsedTickets = Math.max(0, Math.floor(collapsedHeightBudget / perCollapsedTicketPx));

        return Math.max(
            baseTickets,
            Math.min(totalTickets, baseTickets + additionalCollapsedTickets + mediumCollapsedTickets)
        );

        // return Math.max(
        //     minOpen,
        //     Math.min(totalTickets, minOpen + additionalCollapsedTickets + mediumCollapsedTickets)
        // );
    }, [normalizedCardsData.length, safeMinOpenCountForRender, widgetViewportWidth]);

    console.log("Estimated tickets to render based on viewport width:", estimatedTicketsToRender, "for viewport width:", widgetViewportWidth);

    const [maxTicketsToRender, setMaxTicketsToRender] = useState(estimatedTicketsToRender);

    useEffect(() => {
        setMaxTicketsToRender((prev) => {
            const boundedEstimate = Math.max(
                safeMinOpenCountForRender,
                Math.min(normalizedCardsData.length, estimatedTicketsToRender)
            );
            console.log("Adjusting maxTicketsToRender from", prev, "to", boundedEstimate, "based on new estimate:", estimatedTicketsToRender);
            if (boundedEstimate === prev) return prev;
            console.log("Updated maxTicketsToRender to:", boundedEstimate);
            return boundedEstimate;
        });
    }, [estimatedTicketsToRender, normalizedCardsData.length, safeMinOpenCountForRender]);

    const displayCardsData = useMemo(
        () => {
            console.log("Rendering with maxTicketsToRender:", maxTicketsToRender, "out of", normalizedCardsData.length, normalizedCardsData.slice(-maxTicketsToRender));
            return normalizedCardsData.slice(-maxTicketsToRender)
        },
        [normalizedCardsData, maxTicketsToRender]
    );
    const themeStyles = useMemo(() => ({
        cardBackground: isDarkTheme ? "rgb(38, 38, 38)" : "#ffffff",
        cardBorder: isDarkTheme ? "1.5px solid rgb(63, 63, 63)" : "1.5px solid rgba(0,0,0,.125)",
        bodyTextColor: isDarkTheme ? "#b5b5b5" : "inherit",
        mutedTextColor: isDarkTheme ? "#b5b5b5" : "#6c757d",
    }), [isDarkTheme]);

    const [cardStates, setCardStates] = useState(
        displayCardsData.map((_, index) => ({
            isExpanded: index === 0,
            isHalfOpen: false,
            fullHeight: 0,  // Store full height for accurate half-open calculation
            visibleBodyHeight: 0,
            renderMode: index === 0 ? "open" : "collapsed",
            priorCompressedMode: "collapsed",
        }))
    );
    const [isReady, setIsReady] = useState(false);  // Hide cards until first calc completes
    const [disableBodyTransition, setDisableBodyTransition] = useState(true);
    const hasInitiallyLoadedRef = useRef(false);  // Track if we've loaded at least once
    const isMeasuringRef = useRef(false);  // Track if we're in the measurement phase specifically
    const [imageErrors, setImageErrors] = useState({});  // Track image load errors by card id
    const [compactStatusStyle, setCompactStatusStyle] = useState({
        headerPadPx: 4,
        headerMinWidthPx: 10,
        statusPadPx: 2,
        headerFontRem: 0.75,
        statusLabelRem: 0.8,
        statusValueRem: 1,
        bodyPaddingPx: 6,
        avatarSizePx: 50,
        avatarFontPx: 18,
        ownerFontRem: 0.85,
        topLeftFontRem: 0.85,
        titleFontRem: 1,
        descFontRem: 0.85,
        metaFontRem: 0.75,
        titleLines: 1,
        descLines: 2,
        containerPadPx: 2,
        avatarWrapWidthPx: 80,
        showHeaderText: true,
    });

    useLayoutEffect(() => {
        // Increment data version to invalidate any pending auto-reverts
        dataVersionRef.current += 1;

        initialStateRef.current = null;
        hasUserInteractedRef.current = false;
        isFirstCalculationRef.current = true;
        calcRetryRef.current = 0;
        calcRunIdRef.current += 1;
        cardRefs.current = new Array(displayCardsData.length);

        // Clear any pending auto-revert timer when data changes
        if (autoRevertTimerRef.current) {
            clearTimeout(autoRevertTimerRef.current);
            autoRevertTimerRef.current = null;
        }

        // Reset image errors when data changes
        setImageErrors({});

        if (displayCardsData.length === 0) {
            setCardStates([]);
            setIsReady(true);
            setDisableBodyTransition(false);
            hasInitiallyLoadedRef.current = true;
            return;
        }

        // Deterministic refresh: always reset and recalculate to avoid stale layout/state
        isCalculatingRef.current = true;
        if (!hasInitiallyLoadedRef.current) {
            setIsReady(false);
        }
        setDisableBodyTransition(true);
        setCardStates(
            displayCardsData.map((_, index) => ({
                isExpanded: false,
                isHalfOpen: false,
                fullHeight: 0,
                visibleBodyHeight: 0,
                renderMode: "collapsed",
                priorCompressedMode: "collapsed",
            }))
        );

        const scheduleCalc = (delayMs = 0) => {
            const run = () => {
                const doCalc = () => requestAnimationFrame(() => requestAnimationFrame(() => calcHandlerRef.current?.()));
                if (document.fonts && document.fonts.ready) {
                    document.fonts.ready.then(doCalc).catch(doCalc);
                } else {
                    doCalc();
                }
            };
            if (delayMs > 0) return setTimeout(run, delayMs);
            run();
            return null;
        };

        const timer = scheduleCalc(0);

        return () => {
            if (timer) clearTimeout(timer);
            if (autoRevertTimerRef.current) {
                clearTimeout(autoRevertTimerRef.current);
                autoRevertTimerRef.current = null;
            }
        };
    }, [displayCardsData, minOpenTicketCount, isOpenTicketPlacementTop]);

    useEffect(() => {
        // Recalculate after images and fonts load to stabilize first render vs refresh
        const triggerCalc = () => {
            const run = () => requestAnimationFrame(() => requestAnimationFrame(() => calcHandlerRef.current?.()));
            if (document.fonts && document.fonts.ready) {
                document.fonts.ready.then(run).catch(run);
            } else {
                run();
            }
        };

        const timer = setTimeout(() => {
            if (!hasInitiallyLoadedRef.current) {
                triggerCalc();
            }
        }, 300);

        const images = parentRef.current
            ? parentRef.current.querySelectorAll(".card img")
            : [];
        const handleImageLoad = () => triggerCalc();

        images.forEach((img) => {
            if (!img.complete) {
                img.addEventListener("load", handleImageLoad);
                img.addEventListener("error", handleImageLoad);
            }
        });

        return () => {
            clearTimeout(timer);
            images.forEach((img) => {
                img.removeEventListener("load", handleImageLoad);
                img.removeEventListener("error", handleImageLoad);
            });
        };
    }, [displayCardsData]);

    useEffect(() => {
        // Simple window resize handler with debounce
        const handleResize = () => {
            if (resizeDebounceRef.current) {
                clearTimeout(resizeDebounceRef.current);
            }
            resizeDebounceRef.current = setTimeout(() => {
                calcHandlerRef.current?.();
                resizeDebounceRef.current = null;
            }, 300);
        };

        window.addEventListener("resize", handleResize);

        return () => {
            if (resizeDebounceRef.current) {
                clearTimeout(resizeDebounceRef.current);
            }
            window.removeEventListener("resize", handleResize);
            // Clean up auto-revert timer on unmount
            if (autoRevertTimerRef.current) {
                clearTimeout(autoRevertTimerRef.current);
            }
        };
    }, []);

    useEffect(() => {
        calcHandlerRef.current = calculateAndAdjustHeight;
    });

    useLayoutEffect(() => {
        if (!isReady || displayCardsData.length === 0) return;

        const token = layoutMeasureTokenRef.current;
        if (token === appliedMeasureTokenRef.current) return;

        let raf1 = 0;
        let raf2 = 0;
        let settleTimer = null;

        const measureAndApply = () => {
            const gridEl = gridRef.current;
            const cardElements = cardRefs.current.slice(0, displayCardsData.length).filter(Boolean);
            if (!gridEl || cardElements.length === 0) {
                appliedMeasureTokenRef.current = token;
                return;
            }

            const gridStyles = window.getComputedStyle(gridEl);
            const paddingRight = parseFloat(gridStyles.paddingRight || "0") || 0;

            const gridRect = gridEl.getBoundingClientRect();
            const contentRightX = gridRect.left + gridEl.clientWidth - paddingRight;
            const lastCardRightX = cardElements.reduce((maxRight, el) => {
                const rect = el.getBoundingClientRect();
                return Math.max(maxRight, rect.right);
            }, Number.NEGATIVE_INFINITY);

            const trailingEmptySpace = Math.max(0, Number((contentRightX - lastCardRightX).toFixed(3)));

            appliedMeasureTokenRef.current = token;

            if (trailingEmptySpace <= 0.5) return;

            setCardStates((prevStates) => {
                const openIndexes = [];
                prevStates.forEach((state, idx) => {
                    if (state.isExpanded) openIndexes.push(idx);
                });

                if (openIndexes.length === 0) return prevStates;

                const openIndexSet = new Set(openIndexes);
                const extraPerOpenCard = trailingEmptySpace / openIndexes.length;

                const nextStates = prevStates.map((state, idx) => {
                    if (!openIndexSet.has(idx)) return state;
                    return {
                        ...state,
                        visibleBodyHeight: Math.max(0, Number(state.visibleBodyHeight || 0)) + extraPerOpenCard,
                    };
                });

                // Keep auto-revert baseline aligned with post-measure width allocation.
                if (!hasUserInteractedRef.current &&
                    initialStateRef.current &&
                    initialStateRef.current.dataVersion === dataVersionRef.current &&
                    initialStateRef.current.states.length === nextStates.length) {
                    initialStateRef.current = {
                        ...initialStateRef.current,
                        states: JSON.parse(JSON.stringify(nextStates)),
                    };
                }

                return nextStates;
            });
        };

        const settleDelayMs = disableBodyTransition ? 150 : 260;
        raf1 = requestAnimationFrame(() => {
            raf2 = requestAnimationFrame(() => {
                if (settleDelayMs > 0) {
                    settleTimer = setTimeout(measureAndApply, settleDelayMs);
                } else {
                    measureAndApply();
                }
            });
        });

        return () => {
            if (raf1) cancelAnimationFrame(raf1);
            if (raf2) cancelAnimationFrame(raf2);
            if (settleTimer) clearTimeout(settleTimer);
        };
    }, [cardStates, isReady, displayCardsData.length, disableBodyTransition]);

    const calculateAndAdjustHeight = () => {
        if (displayCardsData.length === 0) {
            setIsReady(true);
            setDisableBodyTransition(false);
            return;
        }

        // Allow first calculation to always run, then throttle subsequent ones
        const now = Date.now();
        if (!isFirstCalculationRef.current && now - lastCalcTimeRef.current < 400) return;

        if (!parentRef.current || cardRefs.current.length === 0) {
            if (calcRetryRef.current < 5) {
                calcRetryRef.current += 1;
                setTimeout(calculateAndAdjustHeight, 100);
            }
            return;
        }

        isFirstCalculationRef.current = false; // Mark first calculation as done
        lastCalcTimeRef.current = now;


        const parentWidth = parentRef.current.clientWidth;
        const cardElements = cardRefs.current.slice(0, displayCardsData.length);

        if (cardElements.length === 0 || cardElements.some((el) => !el)) {
            if (calcRetryRef.current < 5) {
                calcRetryRef.current += 1;
                setTimeout(calculateAndAdjustHeight, 100);
            }
            return;
        }

        isCalculatingRef.current = true;
        isMeasuringRef.current = true;
        setDisableBodyTransition(true);



        // Wait a tick for layout to recalculate
        const currentCalcRunId = ++calcRunIdRef.current;
        requestAnimationFrame(() => {

            const restoreMeasuredBodies = () => {
                // originalHeights.forEach((item) => {
                //     if (item) {
                //         item.body.style.maxHeight = item.original.maxHeight;
                //         item.body.style.display = item.original.display;
                //         item.body.style.overflow = item.original.overflow;
                //         item.body.style.padding = item.original.padding;
                //         item.body.style.margin = item.original.margin;
                //         item.body.style.visibility = item.original.visibility;
                //         item.body.style.position = item.original.position;
                //         item.body.style.left = item.original.left;
                //         item.body.style.right = item.original.right;
                //         item.body.style.width = item.original.width;
                //         item.body.style.pointerEvents = item.original.pointerEvents;
                //     }
                // });
            };

            if (currentCalcRunId !== calcRunIdRef.current) {
                //restoreMeasuredBodies();
                isMeasuringRef.current = false;
                setDisableBodyTransition(false);
                return;
            }

            const cardSpacing = cardElements.map((card) => {
                const styles = window.getComputedStyle(card);
                const marginBottom = parseFloat(styles.gap || "3.5");
                return Number.isFinite(marginBottom) ? marginBottom : 3;
            });

            // Calculate layout - use actual rendered grid height to avoid drift from extra paddings/offsets
            const scrollContainer = parentRef.current.querySelector(".slim-scroll");
            //const containerWidth = gridRef.current?.clientHeight || (scrollContainer ? scrollContainer.clientHeight : parentHeight);
            const containerWidth = gridRef.current?.clientWidth || (scrollContainer ? scrollContainer.clientWidth : parentWidth);
            const primaryOpenBodyPx = defaultWidthOfOpenCard;

            // Measure all card body heights
            const cardHeights = cardElements.map((card) => {
                //const body = card.querySelector(".card-body");
                return primaryOpenBodyPx;
                //return body ? body.scrollWidth : 0;
            });

            // Restore original display state
            restoreMeasuredBodies();

            // Measurement phase is complete, allow transitions again for the state updates
            isMeasuringRef.current = false;
            setDisableBodyTransition(false);



            // Dynamically compress header/status area based on available width per card
            const containerPadPx = 2;
            const availableWidth = containerWidth - (containerPadPx * 2);
            const perCardHeight = availableWidth / Math.max(cardElements.length, 1);
            const compressionRatio = Math.max(0, Math.min(1, (90 - perCardHeight) / 60));
            const roomyRatio = 1 - compressionRatio;

            const headerPadPx = 2;  //Math.max(0, Math.round(4 * roomyRatio));
            const headerMinWidthPx = Math.max(2, Math.round(3 + (11 * roomyRatio)));
            const statusPadPx = Math.max(0, Math.round(4 * roomyRatio));
            const headerFontRem = Number((0.56 + (0.26 * roomyRatio)).toFixed(2));

            // Status text - keep standard sizes for clear readability
            const statusLabelRem = 0.8;
            const statusValueRem = 1;

            // Body content - keep standard sizes for clear readability
            const bodyPaddingPx = 6;
            const avatarSizePx = 50;
            const avatarFontPx = 18;
            const ownerFontRem = 0.85;
            const topLeftFontRem = 0.85;
            const titleFontRem = 1;
            const descFontRem = 0.85;
            const metaFontRem = 0.75;
            const titleLines = 2;
            const descLines = 2;

            const avatarWrapWidthPx = 80;
            const showHeaderText = true;

            const newCardStates = displayCardsData.map((_, index) => ({
                isExpanded: false,
                isHalfOpen: false,
                fullHeight: cardHeights[index] || 0,  // Store full height
                visibleBodyHeight: 0,
                renderMode: "collapsed",
                priorCompressedMode: "collapsed",
            }));


            const safeMinOpenCount = Number.isFinite(Number(minOpenTicketCount))
                ? Math.max(1, Math.min(5, Math.floor(Number(minOpenTicketCount))))
                : 1;

            const totalSpacing = cardSpacing.slice(0, -1).reduce((sum, spacing) => sum + spacing, 0);
            setCompactStatusStyle((prev) => {
                if (
                    prev.headerPadPx === headerPadPx &&
                    prev.headerMinWidthPx === headerMinWidthPx &&
                    prev.statusPadPx === statusPadPx &&
                    prev.headerFontRem === headerFontRem &&
                    prev.statusLabelRem === statusLabelRem &&
                    prev.statusValueRem === statusValueRem &&
                    prev.bodyPaddingPx === bodyPaddingPx &&
                    prev.avatarSizePx === avatarSizePx &&
                    prev.avatarFontPx === avatarFontPx &&
                    prev.ownerFontRem === ownerFontRem &&
                    prev.topLeftFontRem === topLeftFontRem &&
                    prev.titleFontRem === titleFontRem &&
                    prev.descFontRem === descFontRem &&
                    prev.metaFontRem === metaFontRem &&
                    prev.titleLines === titleLines &&
                    prev.descLines === descLines &&
                    prev.containerPadPx === containerPadPx &&
                    prev.avatarWrapWidthPx === avatarWrapWidthPx &&
                    prev.showHeaderText === showHeaderText
                ) {
                    return prev;
                }
                return {
                    headerPadPx,
                    headerMinWidthPx: headerMinWidthPx,
                    statusPadPx,
                    headerFontRem,
                    statusLabelRem,
                    statusValueRem,
                    bodyPaddingPx,
                    avatarSizePx,
                    avatarFontPx,
                    ownerFontRem,
                    topLeftFontRem,
                    titleFontRem,
                    descFontRem,
                    metaFontRem,
                    titleLines,
                    descLines,
                    containerPadPx,
                    avatarWrapWidthPx,
                    showHeaderText,
                };
            });

            const normalHeaderPadPxForCalc = 0; //Math.max(2, headerPadPx + 1);
            const normalHeaderMinWidthPxForCalc = defaultOpenHeaderBlockPx; // Math.max(12, headerMinWidthPx + 2);
            const mediumHeaderPadPxForCalc = normalHeaderPadPxForCalc; //Math.max(1, normalHeaderPadPxForCalc - 1);
            const mediumHeaderMinWidthPxForCalc = normalHeaderMinWidthPxForCalc; //Math.max(8, normalHeaderMinWidthPxForCalc - 3);
            const collapsedHeaderPadPxForCalc = 0;// Math.max(0, mediumHeaderPadPxForCalc - 1);
            const collapsedHeaderMinWidthPxForCalc = 4; // Math.max(2, mediumHeaderMinWidthPxForCalc - 5);

            const normalHeaderBlockPxForCalc = normalHeaderMinWidthPxForCalc + (normalHeaderPadPxForCalc * 2);
            const mediumHeaderBlockPxForCalc = mediumHeaderMinWidthPxForCalc + (mediumHeaderPadPxForCalc * 2);
            const collapsedHeaderBlockPxForCalc = collapsedHeaderMinWidthPxForCalc + (collapsedHeaderPadPxForCalc * 2);

            // Baseline all cards as highly-compressed first, then upgrade selected cards to medium/open.
            const compressedHeaderWidth = collapsedHeaderBlockPxForCalc;
            const collapsedBaselineWidth = cardElements.length * compressedHeaderWidth + totalSpacing;
            let availableBodyWidth = Math.max(0, availableWidth - collapsedBaselineWidth);

            // Height-budget based 3-tier grouping:
            // 1) Open tickets (normal) => exactly minOpenTicketCount where possible
            // 2) Medium compressed => consume remaining body budget
            // 3) Highly compressed => header-only collapsed

            const fullOpenFallbackPx = defaultWidthOfOpenCard;

            const buildOpenIndexes = (count) => (count > 0
                ? (isOpenTicketPlacementTop
                    ? Array.from({ length: count }, (_, idx) => idx)
                    : Array.from({ length: count }, (_, idx) => cardElements.length - count + idx))
                : []);

            const targetOpenCount = Math.min(safeMinOpenCount, cardElements.length);
            let fittedOpenCount = targetOpenCount;
            while (fittedOpenCount > 0) {
                const candidateIndexes = buildOpenIndexes(fittedOpenCount);
                const candidateOpenHeights = candidateIndexes.reduce((sum, idx) => {
                    const fullHeight = Math.max(1, Math.floor(cardHeights[idx] || fullOpenFallbackPx));
                    return sum + fullHeight;
                }, 0);
                const candidateHeaderCost = candidateIndexes.length * normalHeaderBlockPxForCalc;
                if ((candidateOpenHeights + candidateHeaderCost) <= availableBodyWidth) {
                    break;
                }
                fittedOpenCount -= 1;
            }

            const openIndexes = buildOpenIndexes(fittedOpenCount);
            const openIndexSet = new Set(openIndexes);
            const openCardsMeta = openIndexes.map((idx) => ({
                idx,
                fullHeight: Math.max(1, Math.floor(cardHeights[idx] || fullOpenFallbackPx)),
            }));

            const applyOpenTicketLayout = (openBodyBudgetPx) => {
                const safeBudget = Math.max(0, Math.floor(openBodyBudgetPx));
                const openCount = openCardsMeta.length;
                if (openCount === 0 || safeBudget <= 0) return 0;

                const allocatedBodies = openCardsMeta.map((item) => item.fullHeight);
                const requiredBudget = allocatedBodies.reduce((sum, value) => sum + value, 0);
                if (safeBudget < requiredBudget) return 0;

                openCardsMeta.forEach((item, orderIdx) => {
                    const visibleBodyHeight = Math.max(1, Math.floor(allocatedBodies[orderIdx]));
                    newCardStates[item.idx].isExpanded = true;
                    newCardStates[item.idx].isHalfOpen = false;
                    newCardStates[item.idx].visibleBodyHeight = visibleBodyHeight;
                    newCardStates[item.idx].renderMode = "open";
                });

                return Math.min(
                    safeBudget,
                    openCardsMeta.reduce((sum, _, idx) => sum + Math.max(1, Math.floor(allocatedBodies[idx])), 0)
                );
            };

            if (openIndexes.length > 0) {
                const openHeaderBudgetPx = openIndexes.length * normalHeaderBlockPxForCalc;
                const openBodyBudgetPx = Math.max(0, availableBodyWidth - openHeaderBudgetPx);

                const consumedOpenBodyPx = applyOpenTicketLayout(openBodyBudgetPx);
                const consumedOpenBudgetPx = openHeaderBudgetPx + consumedOpenBodyPx;
                availableBodyWidth = Math.max(0, availableBodyWidth - consumedOpenBudgetPx);
            }

            const remainingIndexes = [];
            for (let idx = 0; idx < cardElements.length; idx++) {
                if (!openIndexSet.has(idx)) {
                    remainingIndexes.push(idx);
                }
            }



            const applyCompressedModes = (indexes, remainingBodyHeightPx) => {
                const allocationOrder = isOpenTicketPlacementTop
                    ? indexes
                    : [...indexes].reverse();
                const nonOpenCount = indexes.length;
                const dynamicSpaceForCompressedModes = Math.max(0, remainingBodyHeightPx);
                //const mediumUpgradePx = Math.max(1, mediumHeaderBlockPxForCalc - collapsedHeaderBlockPxForCalc);
                const mediumUpgradePx = Math.max(1, mediumHeaderBlockPxForCalc);
                const maxMediumByBudget = Math.min(nonOpenCount, Math.floor(dynamicSpaceForCompressedModes / mediumUpgradePx));

                const mediumCount = maxMediumByBudget; //Math.min(maxMediumByBudget, guaranteedMedium + additionalMediumByRatio);
                const consumedBudgetPx = mediumCount * mediumUpgradePx;

                allocationOrder.forEach((idx, orderIdx) => {
                    const shouldBeMedium = orderIdx < mediumCount;

                    newCardStates[idx].isExpanded = false;
                    newCardStates[idx].isHalfOpen = false;
                    newCardStates[idx].visibleBodyHeight = 0;
                    newCardStates[idx].renderMode = shouldBeMedium ? "medium" : "collapsed";
                    newCardStates[idx].priorCompressedMode = shouldBeMedium ? "medium" : "collapsed";
                });

                return Math.max(0, dynamicSpaceForCompressedModes - consumedBudgetPx);
            };

            applyCompressedModes(remainingIndexes, availableBodyWidth);

            // Mark calculation as complete BEFORE setting states
            isCalculatingRef.current = false;
            // (disableBodyTransition is already set to false after measurement phase)

            if (currentCalcRunId !== calcRunIdRef.current) return;

            layoutMeasureTokenRef.current += 1;
            setCardStates(newCardStates);
            calcRetryRef.current = 0;

            // Store the initial calculated state for auto-revert with data version
            if (!initialStateRef.current) {
                initialStateRef.current = {
                    states: JSON.parse(JSON.stringify(newCardStates)),  // Deep copy
                    dataVersion: dataVersionRef.current
                };
            }

            // Always set ready after calculation completes
            setIsReady(true);
            hasInitiallyLoadedRef.current = true;
        });
    };

    const toggleCard = (index) => {
        // Enable transitions immediately when user toggles a card
        // This ensures animations play for user interactions even during calculation
        hasUserInteractedRef.current = true;
        setDisableBodyTransition(false);
        layoutMeasureTokenRef.current += 1;



        setCardStates((prevStates) => {
            const newStates = [...prevStates];
            const mediumCardCount = newStates.filter((state, stateIndex) => stateIndex !== index && !state.isExpanded && state.renderMode === "medium").length;

            const assignMediumCardsAroundOpen = (openIndex, requestedMediumCount) => {
                if (requestedMediumCount <= 0) return;

                const candidateIndexesAbove = [];
                const candidateIndexesBelow = [];

                for (let i = openIndex - 1; i >= 0; i--) {
                    if (!newStates[i]?.isExpanded) {
                        candidateIndexesAbove.push(i);
                    }
                }

                for (let i = openIndex + 1; i < newStates.length; i++) {
                    if (!newStates[i]?.isExpanded) {
                        candidateIndexesBelow.push(i);
                    }
                }

                const selectedIndexes = candidateIndexesAbove.slice(0, requestedMediumCount);
                if (selectedIndexes.length < requestedMediumCount) {
                    selectedIndexes.push(...candidateIndexesBelow.slice(0, requestedMediumCount - selectedIndexes.length));
                }

                selectedIndexes.forEach((selectedIndex) => {
                    newStates[selectedIndex].renderMode = "medium";
                    newStates[selectedIndex].priorCompressedMode = "medium";
                });
            };

            for (let i = 0; i < newStates.length; i++) {
                if (!newStates[i].isExpanded && newStates[i].renderMode === "medium") {
                    newStates[i].renderMode = "collapsed";
                    newStates[i].priorCompressedMode = "collapsed";
                }
            }

            const setClosedPriorMode = (state) => {
                state.isExpanded = false;
                state.isHalfOpen = false;
                state.visibleBodyHeight = 0;
                state.renderMode = state.priorCompressedMode === "medium" ? "medium" : "collapsed";
            };
            const previousOpenBodyBudget = prevStates.reduce((sum, state) => {
                if (!state?.isExpanded) return sum;
                const measured = Number(state.visibleBodyHeight || 0);
                const fallback = Number(state.fullHeight || defaultWidthOfOpenCard);
                return sum + Math.max(1, measured > 0 ? measured : fallback);
            }, 0);

            const rebalanceOpenBodyWidths = () => {
                const openIndexes = [];
                let baseBudget = 0;

                newStates.forEach((state, idx) => {
                    if (!state?.isExpanded) return;
                    const fullWidth = Math.max(1, Number(state.fullHeight || defaultWidthOfOpenCard));
                    state.visibleBodyHeight = fullWidth;
                    state.renderMode = "open";
                    openIndexes.push(idx);
                    baseBudget += fullWidth;
                });

                if (openIndexes.length === 0) return;

                const targetBudget = Math.max(baseBudget, previousOpenBodyBudget);
                const extraPerOpenCard = (targetBudget - baseBudget) / openIndexes.length;
                if (extraPerOpenCard <= 0) return;

                openIndexes.forEach((idx) => {
                    newStates[idx].visibleBodyHeight = Math.max(1, Number(newStates[idx].visibleBodyHeight || 0) + extraPerOpenCard);
                });
            };

            const currentlyExpanded = newStates[index].isExpanded;
            const currentlyHalfOpen = newStates[index].isHalfOpen;
            const isCurrentlyClosed = !currentlyExpanded && !currentlyHalfOpen;

            const openNextTicket = (closedIndex) => {
                if (!Array.isArray(newStates) || newStates.length <= 1) return;

                const step = isOpenTicketPlacementTop ? 1 : -1;
                const length = newStates.length;

                const tryOpenCandidate = (candidateIndex) => {
                    const candidateState = newStates[candidateIndex];
                    if (!candidateState || candidateState.isExpanded || candidateState.isHalfOpen) return false;

                    candidateState.priorCompressedMode = candidateState.renderMode === "medium" ? "medium" : "collapsed";
                    candidateState.isExpanded = true;
                    candidateState.isHalfOpen = false;
                    candidateState.visibleBodyHeight = candidateState.fullHeight || 0;
                    candidateState.renderMode = "open";
                    return candidateIndex;
                };

                const nextIndex = (closedIndex + step + length) % length;
                const previousIndex = (closedIndex - step + length) % length;

                // Only consider immediate neighbor first, then the opposite neighbor.
                const nextOpenedIndex = tryOpenCandidate(nextIndex);
                if (nextOpenedIndex !== false) {
                    return nextOpenedIndex;
                }

                return tryOpenCandidate(previousIndex);
            };

            const safeMinOpenCount = Number.isFinite(Number(minOpenTicketCount))
                ? Math.max(1, Math.min(5, Math.floor(Number(minOpenTicketCount))))
                : 1;

            // If half-open, expand to full
            if (currentlyHalfOpen) {
                newStates[index].isHalfOpen = false;
                newStates[index].isExpanded = true;
                newStates[index].visibleBodyHeight = newStates[index].fullHeight || 0;
                newStates[index].renderMode = "open";

                // Close other expanded cards to make room, keeping minOpenTicketCount
                const openCount = newStates.filter(s => s.isExpanded || s.isHalfOpen).length;
                if (openCount > safeMinOpenCount) {
                    const closeStart = isOpenTicketPlacementTop ? newStates.length - 1 : 0;
                    const closeEnd = isOpenTicketPlacementTop ? -1 : newStates.length;
                    const closeStep = isOpenTicketPlacementTop ? -1 : 1;
                    for (let i = closeStart; i !== closeEnd; i += closeStep) {
                        if (i !== index && newStates[i].isExpanded) {
                            setClosedPriorMode(newStates[i]);
                            break;
                        }
                    }
                }
            }
            // If opening a closed card, check if we need to close another to maintain space
            else if (isCurrentlyClosed) {
                // Requirement: when opening a ticket, close any other fully expanded ticket first.
                const closeStart = isOpenTicketPlacementTop ? newStates.length - 1 : 0;
                const closeEnd = isOpenTicketPlacementTop ? -1 : newStates.length;
                const closeStep = isOpenTicketPlacementTop ? -1 : 1;
                let fullyExpandedClosed = false;

                for (let i = closeStart; i !== closeEnd; i += closeStep) {
                    if (i !== index && newStates[i].isExpanded) {
                        setClosedPriorMode(newStates[i]);
                        fullyExpandedClosed = true;
                        break;
                    }
                }

                // Count currently open/half-open cards
                const openCount = newStates.filter(s => s.isExpanded || s.isHalfOpen).length;

                // If still at or over the configured limit, close one more open/half-open card.
                if (!fullyExpandedClosed && openCount >= safeMinOpenCount) {
                    for (let i = closeStart; i !== closeEnd; i += closeStep) {
                        if (i !== index && (newStates[i].isExpanded || newStates[i].isHalfOpen)) {
                            setClosedPriorMode(newStates[i]);
                            break;
                        }
                    }
                }

                newStates[index].priorCompressedMode = newStates[index].renderMode === "medium" ? "medium" : "collapsed";
                // Now open the clicked card as fully expanded
                newStates[index].isExpanded = true;
                newStates[index].isHalfOpen = false;
                newStates[index].visibleBodyHeight = newStates[index].fullHeight || 0;
                newStates[index].renderMode = "open";
                assignMediumCardsAroundOpen(index, mediumCardCount);
            } else {
                // Fully expanded - collapse it completely
                setClosedPriorMode(newStates[index]);

                // When closing an open ticket, automatically open the next ticket
                openNextTicket(index);
                const autoOpenedIndex = openNextTicket(index);
                if (autoOpenedIndex !== false && autoOpenedIndex !== undefined) {
                    assignMediumCardsAroundOpen(autoOpenedIndex, mediumCardCount);
                }
            }

            rebalanceOpenBodyWidths();

            return newStates;
        });

        // Clear any existing timer
        if (autoRevertTimerRef.current) {
            clearTimeout(autoRevertTimerRef.current);
        }

        // Set new timer to revert if autoRevertDurationinSec is greater than 0
        if (autoRevertDurationinSec > 0) {
            const currentVersion = dataVersionRef.current;
            autoRevertTimerRef.current = setTimeout(() => {
                // Only restore if data hasn't changed
                if (initialStateRef.current &&
                    initialStateRef.current.dataVersion === currentVersion) {
                    setCardStates((prevStates) => {
                        if (!initialStateRef.current || initialStateRef.current.states.length !== prevStates.length) {
                            return prevStates;
                        }
                        layoutMeasureTokenRef.current += 1;
                        return JSON.parse(JSON.stringify(initialStateRef.current.states));
                    });
                }
                autoRevertTimerRef.current = null;
            }, autoRevertDurationinSec * 1000);
        }
    };

    const formatOwnerName = (name) => {
        if (!name) return "";
        const words = name.trim().split(/\s+/);
        if (words.length === 1) return words[0];

        // First word as is, rest as initials
        const formatted = [words[0]];
        for (let i = 1; i < words.length; i++) {
            formatted.push(words[i][0].toUpperCase());
        }
        return formatted.join(" ");
    };

    const getInitials = (name) => {
        if (!name) return "?";
        const words = name.trim().split(/\s+/);
        if (words.length === 1) return words[0][0]?.toUpperCase() || "?";
        // First letter of first word + first letter of last word
        return (words[0][0] + words[words.length - 1][0]).toUpperCase();
    };

    const handleImageError = (cardId) => {
        setImageErrors((prev) => ({ ...prev, [cardId]: true }));
    };

    const shouldShowCards = isReady || hasInitiallyLoadedRef.current;

    return (
        <div ref={parentRef} className={classes.landscapeRoot} style={{ height: "100%", width: "100%" }}>
            <div
                className={`slim-scroll ${classes.landscapeScroll}`}
                style={{
                    width: "100%",
                    height: "100%",
                    overflow: "hidden",
                    opacity: shouldShowCards ? 1 : 0,
                    visibility: shouldShowCards ? "visible" : "hidden",
                    transition: "none",
                }}
            >
                <div
                    className={classes.landscapeGrid}
                    ref={gridRef}
                    style={{
                        paddingTop: "0px",
                        paddingLeft: `${compactStatusStyle.containerPadPx}px`,
                        paddingRight: `${compactStatusStyle.containerPadPx}px`,
                        paddingBottom: "0px",
                        display: "flex",
                        flexDirection: "row",
                        flexWrap: "nowrap",
                        alignItems: "stretch",
                        alignContent: "flex-start",
                        gap: "3px",
                        height: "100%",
                        boxSizing: "border-box",
                    }}
                >
                    {displayCardsData.map((card, index) => (
                        (() => {
                            //console.log("Rendering card index:", index, "with state:", cardStates[index]);
                            const cardState = cardStates[index] || {};
                            const isExpanded = Boolean(cardState.isExpanded);
                            const isHalfOpen = Boolean(cardState.isHalfOpen);
                            const renderMode = (cardState.renderMode === "open" || isExpanded || isHalfOpen)
                                ? "open"
                                : (cardState.renderMode === "medium" ? "medium" : "collapsed");
                            const isCollapsedMode = renderMode === "collapsed";
                            const isMediumMode = renderMode === "medium";
                            const isBodyOpen = renderMode === "open";
                            const fullWidth = Number(cardState.fullHeight || 0);
                            const visibleBodyWidth = Number(cardState.visibleBodyHeight || 0);
                            const computedVisibleWidth = renderMode === "open"
                                ? Math.max(1, visibleBodyWidth || fullWidth)
                                : 0;
                            const bodyWidth = renderMode === "open"
                                ? `${Math.max(0, computedVisibleWidth).toFixed(3)}px`
                                : "0px";
                            const bodyDisplay = "flex";
                            const bodyPadding = renderMode === "open" ? `${compactStatusStyle.bodyPaddingPx}px` : "0";
                            const bodyMargin = "0";
                            const cardTransform = isReady ? "translateY(0px)" : "translateY(4px)";
                            const cardEntryDelayMs = Math.min(index, 8) * 36;
                            const contentOpacity = isBodyOpen ? 1 : 0;
                            const contentTransition = disableBodyTransition
                                ? "none"
                                : "opacity 140ms linear";
                            const normalHeaderPadPx = 2; // Math.max(2, compactStatusStyle.headerPadPx + 1);
                            const normalHeaderMinWidthPx = 12; //  Math.max(12, compactStatusStyle.headerMinWidthPx + 2);
                            const normalHeaderFontRem = 0.76; // Math.max(0.76, compactStatusStyle.headerFontRem + 0.08);

                            const mediumHeaderPadPx = normalHeaderPadPx; // Math.max(1, normalHeaderPadPx - 1);
                            const mediumHeaderMinWidthPx = normalHeaderMinWidthPx; // Math.max(8, normalHeaderMinWidthPx - 3);
                            const mediumHeaderFontRem = normalHeaderFontRem; // Math.max(0.64, Number((normalHeaderFontRem - 0.1).toFixed(2)));

                            const collapsedHeaderPadPx = 0; // Math.max(0, mediumHeaderPadPx - 1);
                            const collapsedHeaderMinWidthPx = 2; // Math.max(4, mediumHeaderMinWidthPx - 5);
                            const collapsedHeaderFontRem = Math.max(0.44, Number((mediumHeaderFontRem - 0.18).toFixed(2)));

                            const headerPadPx = isCollapsedMode
                                ? collapsedHeaderPadPx
                                : isMediumMode
                                    ? mediumHeaderPadPx
                                    : normalHeaderPadPx;
                            const headerFontSize = isCollapsedMode
                                ? `${collapsedHeaderFontRem}rem`
                                : isMediumMode
                                    ? `${mediumHeaderFontRem}rem`
                                    : `${normalHeaderFontRem}rem`;
                            const headerLineHeight = isCollapsedMode ? 0.5 : isMediumMode ? 0.7 : 1;

                            const headerDisplayText = `${isCollapsedMode ? "" : (card.headerText || "")}`;
                            //const headerTextWidthPx = Math.max(8, Math.ceil(parseFloat(headerFontSize) * 16));
                            const headerWidthPx = isCollapsedMode
                                ? collapsedHeaderMinWidthPx
                                : isMediumMode
                                    ? mediumHeaderMinWidthPx
                                    : normalHeaderMinWidthPx;

                            return (
                                <div
                                    className={`card ${classes.landscapeCard}`}
                                    key={card.id}
                                    ref={(el) => (cardRefs.current[index] = el)}
                                    style={{
                                        backgroundColor: index % 2 !== 0 ? (isDarkTheme ? "#333333" : "transparent") : "transparent",
                                        border: isCollapsedMode || isMediumMode ? "none" : themeStyles.cardBorder,
                                        color: themeStyles.bodyTextColor,
                                        borderRadius: isCollapsedMode || isMediumMode ? "0" : "5px",
                                        opacity: isReady ? 1 : 0,
                                        transform: cardTransform,
                                        transition: `opacity 500ms cubic-bezier(0.22, 1, 0.36, 1) ${cardEntryDelayMs}ms, transform 500ms cubic-bezier(0.22, 1, 0.36, 1) ${cardEntryDelayMs}ms`,
                                        display: "flex",
                                        flexDirection: "row",
                                        alignItems: "stretch",
                                        flex: "0 0 auto",
                                        maxWidth: "none",
                                        minWidth: "0px",
                                        height: isCollapsedMode || isMediumMode ? "calc(100% - 4px)" : "calc(100% - 4px)",
                                        minHeight: 0,
                                        boxSizing: "border-box",
                                        overflow: "hidden",
                                        marginTop: isCollapsedMode || isMediumMode ? "2px" : "0",
                                    }}
                                    onMouseEnter={(event) => {
                                        if (!isReady) return;
                                        event.currentTarget.style.transform = "translateY(-1px)";
                                    }}
                                    onMouseLeave={(event) => {
                                        if (!isReady) return;
                                        event.currentTarget.style.transform = "translateY(0px)";
                                    }}
                                >
                                    <div
                                        className={`card-header fw-bold ${classes.landscapeHeader}`}
                                        style={{
                                            cursor: "pointer",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                            paddingTop: `${headerPadPx}px`,
                                            paddingBottom: `${headerPadPx}px`,
                                            paddingRight: `${headerPadPx}px`,
                                            paddingLeft: `${headerPadPx}px`,
                                            minHeight: 0,
                                            boxSizing: "border-box",
                                            fontSize: headerFontSize,
                                            lineHeight: headerLineHeight,
                                            backgroundColor: colors[index % colors.length],
                                            border: `1px solid ${colors[index % colors.length]}`,
                                            color: compactStatusStyle.showHeaderText ? "#ffffff" : "transparent",
                                            borderTopLeftRadius: isCollapsedMode || isMediumMode ? "0" : "4px",
                                            borderTopRightRadius: isCollapsedMode || isMediumMode ? "0" : "4px",
                                            display: "flex",
                                            alignItems: "flex-start",
                                            justifyContent: "flex-start",
                                            alignSelf: "stretch",
                                            textAlign: "center",

                                            transition: "color 180ms ease",
                                            flex: `0 0 ${headerWidthPx}px`,
                                            maxWidth: `${headerWidthPx}px`,
                                            minWidth: `${headerWidthPx}px`,
                                            height: "100%",
                                            maxHeight: "100%",
                                            wordBreak: "normal",
                                            writingMode: "vertical-rl",
                                            textOrientation: "mixed",
                                            transform: "rotate(180deg)",
                                        }}
                                        title={headerDisplayText}
                                        onClick={() => toggleCard(index)}
                                    >
                                        &nbsp;&nbsp;{headerDisplayText}
                                    </div>

                                    <div
                                        className={`card-body collapse show ${classes.landscapeBody}`}
                                        style={{
                                            backgroundColor: index % 2 !== 0 ? (isDarkTheme ? "#333333" : "transparent") : "transparent",
                                            color: themeStyles.bodyTextColor,
                                            overflow: "hidden",
                                            transition: disableBodyTransition ? "none" : "flex-basis 240ms cubic-bezier(0.22, 1, 0.36, 1), opacity 180ms linear",
                                            maxHeight: "100%",
                                            flexBasis: bodyWidth,
                                            minWidth: 0,
                                            display: bodyDisplay,
                                            padding: bodyPadding,
                                            margin: bodyMargin,
                                            opacity: isBodyOpen ? 1 : 0,
                                            pointerEvents: isBodyOpen ? "auto" : "none",
                                            borderRadius: "0",
                                            flexGrow: 0,
                                            flexShrink: 0,
                                            alignSelf: "stretch",
                                            boxSizing: "border-box",
                                            contain: "layout paint",
                                            transform: "translateZ(0)",
                                            backfaceVisibility: "hidden",
                                            willChange: disableBodyTransition ? "auto" : "flex-basis, opacity",
                                        }}
                                    >
                                        <div className={`${classes.landscapeBodySection} ${classes.landscapeBodySectionTop}`}>
                                            <div
                                                className="row align-items-center"
                                                style={{
                                                    display: "flex",
                                                    flexWrap: "nowrap",
                                                    marginLeft: 0,
                                                    marginRight: 0,
                                                    width: "100%",
                                                    opacity: contentOpacity,
                                                    transition: contentTransition,
                                                }}
                                            >
                                                <div className="col text-start" style={{ flex: "1 1 0", minWidth: 0, maxWidth: "none", paddingLeft: 0, paddingRight: 0 }}>
                                                    <div
                                                        style={{
                                                            overflow: "hidden",
                                                            textOverflow: "ellipsis",
                                                            whiteSpace: "nowrap",
                                                            maxWidth: "120px",
                                                            fontSize: `${compactStatusStyle.topLeftFontRem}rem`,
                                                        }}
                                                        title={card.topLeftText}
                                                    >
                                                        {card.topLeftText}
                                                    </div>
                                                </div>
                                                <div
                                                    className="col text-center"
                                                    style={{
                                                        flex: "1 1 0",
                                                        minWidth: 0,
                                                        maxWidth: "none",
                                                        paddingTop: `${compactStatusStyle.statusPadPx}px`,
                                                        paddingBottom: `${Math.max(1, Math.floor(compactStatusStyle.statusPadPx / 2))}px`,
                                                    }}
                                                >
                                                    <div style={{ fontSize: `${compactStatusStyle.statusLabelRem}rem`, lineHeight: 1 }}>Status</div>
                                                    <div className="fw-bold" style={{ fontSize: `${compactStatusStyle.statusValueRem}rem`, lineHeight: 1.1 }}>{card.percent}</div>
                                                </div>

                                                <div className="col d-flex flex-column align-items-end" style={{ flex: "1 1 0", minWidth: 0, maxWidth: "none", paddingLeft: 0, paddingRight: 0 }}>
                                                    <div style={{ width: `${compactStatusStyle.avatarWrapWidthPx}px`, marginLeft: "auto", display: "flex", justifyContent: "center" }}>
                                                        {!card.imageUrl || imageErrors[card.id] ? (
                                                            <div
                                                                style={{
                                                                    width: compactStatusStyle.avatarSizePx,
                                                                    height: compactStatusStyle.avatarSizePx,
                                                                    borderRadius: "50%",
                                                                    backgroundColor: colors[0] || "#5f8b95",
                                                                    color: isDarkTheme ? "#ffffff" : "white",
                                                                    display: "flex",
                                                                    alignItems: "center",
                                                                    justifyContent: "center",
                                                                    fontSize: `${compactStatusStyle.avatarFontPx}px`,
                                                                    fontWeight: "bold",
                                                                }}
                                                            >
                                                                {getInitials(card.owner)}
                                                            </div>
                                                        ) : (
                                                            <img
                                                                src={card.imageUrl}
                                                                alt="avatar"
                                                                style={{
                                                                    width: compactStatusStyle.avatarSizePx,
                                                                    height: compactStatusStyle.avatarSizePx,
                                                                    borderRadius: "50%",
                                                                }}
                                                                onError={() => handleImageError(card.id)}
                                                            />
                                                        )}
                                                    </div>

                                                    <div
                                                        style={{
                                                            width: `${compactStatusStyle.avatarWrapWidthPx}px`,
                                                            marginLeft: "auto",
                                                            overflow: "hidden",
                                                            textOverflow: "ellipsis",
                                                            whiteSpace: "nowrap",
                                                            textAlign: "center",
                                                            color: themeStyles.bodyTextColor,
                                                            fontSize: `${compactStatusStyle.ownerFontRem}rem`,
                                                        }}
                                                        title={card.owner}
                                                    >
                                                        {formatOwnerName(card.owner)}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className={`${classes.landscapeBodySection} ${classes.landscapeBodySectionMiddle}`}>
                                            <div className={classes.landscapeBodyStack}>
                                                <p
                                                    className="mt-2 fw-bold text-center"
                                                    style={{
                                                        display: "-webkit-box",
                                                        WebkitLineClamp: compactStatusStyle.titleLines,
                                                        WebkitBoxOrient: "vertical",
                                                        overflow: "hidden",
                                                        textOverflow: "ellipsis",
                                                        fontSize: `${compactStatusStyle.titleFontRem}rem`,
                                                        lineHeight: 1.2,
                                                        marginBottom: "0.25rem",
                                                        opacity: contentOpacity,
                                                        transition: contentTransition,
                                                    }}
                                                    title={card.title}
                                                >
                                                    {card.title}
                                                </p>
                                                <p
                                                    className="mt-1 text-center"
                                                    style={{
                                                        color: themeStyles.mutedTextColor,
                                                        display: "-webkit-box",
                                                        WebkitLineClamp: compactStatusStyle.descLines,
                                                        WebkitBoxOrient: "vertical",
                                                        overflow: "hidden",
                                                        textOverflow: "ellipsis",
                                                        fontSize: `${compactStatusStyle.descFontRem}rem`,
                                                        lineHeight: 1.2,
                                                        marginBottom: "0.4rem",
                                                        opacity: contentOpacity,
                                                        transition: contentTransition,
                                                    }}
                                                    title={card.description}
                                                >
                                                    {card.description}
                                                </p>
                                            </div>
                                        </div>

                                        <div className={`${classes.landscapeBodySection} ${classes.landscapeBodySectionBottom}`}>
                                            <div
                                                className="row small"
                                                style={{
                                                    display: "flex",
                                                    flexWrap: "nowrap",
                                                    marginLeft: 0,
                                                    marginRight: 0,
                                                    width: "100%",
                                                    fontSize: `${compactStatusStyle.metaFontRem}rem`,
                                                    lineHeight: 1.2,
                                                    opacity: contentOpacity,
                                                    transition: contentTransition,
                                                }}
                                            >
                                                <div className="col text-start" style={{ flex: "1 1 0", minWidth: 0, maxWidth: "none", paddingLeft: 0, paddingRight: 0 }}>
                                                    <b>Opened</b>
                                                    <br />
                                                    {card.openDate}
                                                </div>
                                                <div className="col text-center" style={{ flex: "1 1 0", minWidth: 0, maxWidth: "none", paddingLeft: 0, paddingRight: 0 }}>
                                                    <b>Promised</b>
                                                    <br />
                                                    {card.promiseDate}
                                                </div>
                                                <div className="col d-flex flex-column align-items-end" style={{ flex: "1 1 0", minWidth: 0, maxWidth: "none", paddingLeft: 0, paddingRight: 0 }}>
                                                    <div style={{
                                                        width: `${compactStatusStyle.avatarWrapWidthPx}px`,
                                                        marginLeft: "auto",
                                                        overflow: "hidden",
                                                        textOverflow: "ellipsis",
                                                        whiteSpace: "nowrap",
                                                        textAlign: "center",
                                                        color: themeStyles.bodyTextColor,

                                                    }}
                                                        title={card.status}
                                                    >
                                                        <b>&nbsp;&nbsp;&nbsp;{card.status}</b>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })()
                    ))}
                </div>
            </div>
        </div>
    );
}
